import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { IProductionDashboardStepDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import * as ProductionService from "../../services/ProductionService";
import { ProductionAuthContext } from "../../services/ProductionService";
import IOwTableCols from "../../models/IOwTableCols";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import PaginationBar from "../shared/PaginationBar/PaginationBar";
import SerialWorkflowHistory from "./SerialWorkflowHistory";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";

interface ProductionStepDrilldownProps {
    step: IProductionDashboardStepDTO;
    ctx: ProductionAuthContext;
    onBack: () => void;
}

const PAGE_SIZE = 25;

const ProductionStepDrilldown: React.FC<ProductionStepDrilldownProps> = ({ step, ctx, onBack }) => {
    const [serials, setSerials] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);
    const [historySerialId, setHistorySerialId] = useState<number | null>(null);

    const fetchSerials = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await ProductionService.getSerialsByStep(ctx, step.stepId ?? 0, page, PAGE_SIZE);
            setSerials(data.serials);
            setTotalCount(data.totalCount);
        } catch (err) {
            console.error("Failed to fetch step serials", err);
        } finally {
            setIsLoading(false);
            setIsInitialData(false);
        }
    }, [step.stepId, page]);

    useEffect(() => {
        fetchSerials();
    }, [fetchSerials]);

    const columns: IOwTableCols[] = [
        {
            title: "serial",
            valueProp: "value",
            renderFn: (row: any) => {
                return (
                    <span className="fw-bold text-primary" style={{ cursor: "pointer" }} onClick={() => setHistorySerialId(row.id)}>
                        {row.value}
                    </span>
                );
            },
            renderFnEmitsRow: true,
        },
        {
            title: "model",
            valueProp: "Catalog_Item",
            renderFn: (row: any) => row.Catalog_Item?.name || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "caliber",
            valueProp: "Catalog_Item",
            renderFn: (row: any) => row.Catalog_Item?.caliber || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "time_at_step",
            valueProp: "current_step_entered_at",
            renderFn: (row: any) => {
                const enteredAt = row.current_step_entered_at || row.updated_at;
                return enteredAt ? moment(enteredAt).fromNow(true) : "—";
            },
            renderFnEmitsRow: true,
        },
        {
            title: "last_log",
            valueProp: "Last_Log",
            renderFn: (row: any) => {
                if (!row.Last_Log) return "—";
                return (
                    <>
                        {row.Last_Log.Employee_User?.name && <span className="me-2">{row.Last_Log.Employee_User.name}</span>}
                        {row.Last_Log.completed_at && (
                            <small className="text-muted">{moment(row.Last_Log.completed_at).format("MM/DD hh:mm A")}</small>
                        )}
                    </>
                );
            },
            renderFnEmitsRow: true,
        },
    ];

    return (
        <div className="pd-drilldown">
            <div className="pd-drilldown__header">
                <button className="btn btn-outline-secondary btn-sm" onClick={onBack}>
                    <i className="fas fa-arrow-left me-1" /> Back
                </button>
                <h4 className="pd-drilldown__title mb-0 ms-3">
                    <OwTranslate id={step.stepName} defaultMessage={step.stepName} />
                    <span className="text-muted ms-2" style={{ fontSize: "0.875rem" }}>
                        ({step.serialCount} serials)
                    </span>
                </h4>
            </div>

            <OwFnTable
                columns={columns}
                dataSet={serials}
                dataFetch={isLoading}
                initialData={isInitialData}
                tableConfig={{ tableHover: true }}
                rowKeyProp="id"
            />

            {totalCount > PAGE_SIZE && (
                <PaginationBar
                    offset={page * PAGE_SIZE}
                    limit={PAGE_SIZE}
                    totalRecords={totalCount}
                    onPaginationChanged={(newOffset: number) => {
                        setPage(Math.floor(newOffset / PAGE_SIZE));
                    }}
                />
            )}

            <SerialWorkflowHistory
                show={historySerialId !== null}
                serialId={historySerialId}
                ctx={ctx}
                onClose={() => setHistorySerialId(null)}
            />
        </div>
    );
};

export default ProductionStepDrilldown;
